import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { createExercise } from "../apis/exercisesApi";
import { STORAGE_BASE_URL } from "../config/api";
import Swal from "sweetalert2";
import BackgroundSelector from "../components/BackgroundSelector";
import { library } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleArrowLeft, faCaretDown, faTrash } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import "../styles.css";

library.add(faCircleArrowLeft, faCaretDown, faTrash);

const EventForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBackgroundSelector, setShowBackgroundSelector] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);
  const [errorField, setErrorField] = useState(null);
  const [exerciseData, setExerciseData] = useState({
    name: "",
    help_image: null,
    background_image_path: null,
    consigne: "",
    consigne_audio: null,
    song_audio: null,
    type: "",
    fonttype: "",
    per_link: "",
    isFrench: false,
    isArabic: false,
    questions: [],
  });
  const [backgroundImageFile, setBackgroundImageFile] = useState(null);
  const [questions, setQuestions] = useState(
    Array(6)
      .fill()
      .map(() => ({
        T1: "",
        T2: "",
        T3: "",
        T4: "",
        T5: "",
        T6: "",
        T7: "",
        T9: "",
        fr1: "",
        table4: null,
        hmed_audio: null,
        Isac_audio: null,
        reponse: "",
        reponse1: "",
        reponse2: "",
        phrase_finale: "",
      }))
  );
  const [questionData, setQuestionData] = useState({
    T1: "",
    T2: "",
    T3: "",
    T4: "",
    T5: "",
    T6: "",
    T7: "",
    T9: "",
    fr1: "",
    table4: null,
    hmed_audio: null,
    Isac_audio: null,
    reponse: "",
    reponse1: "",
    reponse2: "",
    phrase_finale: "",
  });
  const [questionFiles, setQuestionFiles] = useState(
    Array(6)
      .fill()
      .map(() => ({ table4: null, hmed_audio: null, Isac_audio: null }))
  );

  const helpImageRef = useRef(null);
  const consigneAudioRef = useRef(null);
  const songAudioRef = useRef(null);
  const table4Ref = useRef(null);
  const hmedAudioRef = useRef(null);
  const IsacAudioRef = useRef(null);
  const backgroundImageRef = useRef(null);
  const selectRef = useRef(null);
  const fonttypeSelectRef = useRef(null);

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB for regular files
const MAX_BACKGROUND_SIZE = 50 * 1024 * 1024; // 50MB for background images
const MAX_TABLE4_SIZE = 2 * 1024 * 1024; // 2MB for table4 images
  const MAX_QUESTIONS = 6;

  const generateDraftId = () => {
    return `draft-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  };

  useEffect(() => {
    const draftFromNav = location.state?.draft;
    if (draftFromNav) {
      setExerciseData({
        ...draftFromNav.exerciseData,
        help_image: null,
        consigne_audio: null,
        song_audio: null,
        isFrench: !!draftFromNav.exerciseData.isFrench,
        isArabic: !!draftFromNav.exerciseData.isArabic,
      });
      setBackgroundImageFile(null);
      setQuestions(
        draftFromNav.questions.map((q) => ({
          ...q,
          table4: null,
          hmed_audio: null,
          Isac_audio: null,
        }))
      );
      setQuestionData({
        ...draftFromNav.questionData,
        table4: null,
        hmed_audio: null,
        Isac_audio: null,
      });
      setQuestionFiles(
        Array(6).fill({ table4: null, hmed_audio: null, Isac_audio: null })
      );
      setCurrentQuestionIndex(draftFromNav.currentQuestionIndex);
    }
  }, [location]);

  const handleSaveAsDraft = () => {
    const draft = {
      id: generateDraftId(),
      timestamp: Date.now(),
      exerciseData: {
        ...exerciseData,
        help_image: null,
        consigne_audio: null,
        song_audio: null,
        isFrench: !!exerciseData.isFrench,
        isArabic: !!exerciseData.isArabic,
      },
      questions: questions.map((q) => ({
        ...q,
        table4: null,
        hmed_audio: null,
        Isac_audio: null,
      })),
      questionData: {
        ...questionData,
        table4: null,
        hmed_audio: null,
        Isac_audio: null,
      },
      currentQuestionIndex,
    };
    const existingDraftsStr = localStorage.getItem("exerciseDrafts");
    let existingDrafts = existingDraftsStr ? JSON.parse(existingDraftsStr) : [];
    existingDrafts.push(draft);
    localStorage.setItem("exerciseDrafts", JSON.stringify(existingDrafts));
    Swal.fire({
      title: t("success"),
      text: t("draftSaved") || "Draft saved successfully",
      icon: "success",
      confirmButtonColor: "var(--primary-color)",
      background: "var(--card-background)",
    });
  };

  const handleExerciseChange = (e) => {
  const { name, value, files, type, checked } = e.target;
  setErrorField((prev) => (prev === name ? null : prev));
  if (files && (name === "help_image" || name === "consigne_audio" || name === "song_audio" || name === "background_image")) {
    const file = files[0];
    if (!file) return;

    // Separate size validation for background images (50MB) vs other files (10MB)
    if (name === "background_image") {
      if (file.size > 50 * 1024 * 1024) { // Changed to 50MB
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: t("fileTooLarge", { size: 50 }), // Changed size parameter to 50
          confirmButtonColor: "var(--primary-color)",
          background: "var(--card-background)",
        });
        e.target.value = null;
        backgroundImageRef.current.value = null;
        return;
      }
    } else {
      if (file.size > MAX_FILE_SIZE) { // Other files still 10MB
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: t("fileTooLarge", { size: 10 }),
          confirmButtonColor: "var(--primary-color)",
          background: "var(--card-background)",
        });
        e.target.value = null;
        if (name === "help_image") helpImageRef.current.value = null;
        else if (name === "consigne_audio") consigneAudioRef.current.value = null;
        else if (name === "song_audio") songAudioRef.current.value = null;
        return;
      }
    }

    if (
      (name === "help_image" || name === "background_image") &&
      !["image/jpeg", "image/png", "image/jpg", "image/gif"].includes(file.type)
    ) {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t(`invalidImageType.${name === "help_image" ? "help" : "background"}`),
        confirmButtonColor: "var(--primary-color)",
        background: "var(--card-background)",
      });
      e.target.value = null;
      if (name === "help_image") helpImageRef.current.value = null;
      else backgroundImageRef.current.value = null;
      return;
    }

    if ((name === "consigne_audio" || name === "song_audio") && file.type !== "audio/wav") {
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: t(`invalidAudioType.${name === "consigne_audio" ? "consigne" : "song"}`),
        confirmButtonColor: "var(--primary-color)",
        background: "var(--card-background)",
      });
      e.target.value = null;
      if (name === "consigne_audio") consigneAudioRef.current.value = null;
      else if (name === "song_audio") songAudioRef.current.value = null;
      return;
    }

    if (name === "background_image") {
      setBackgroundImageFile(file);
      setExerciseData((prev) => ({ ...prev, background_image_path: null }));
    } else {
      setExerciseData((prev) => ({ ...prev, [name]: file }));
    }
  } else if (type === "checkbox" && name === "isFrench") {
    setExerciseData((prev) => ({
      ...prev,
      isFrench: checked,
      isArabic: checked ? false : prev.isArabic
    }));
  } else if (type === "checkbox" && name === "isArabic") {
    setExerciseData((prev) => ({
      ...prev,
      isArabic: checked,
      isFrench: checked ? false : prev.isFrench
    }));
  } else {
    setExerciseData((prev) => ({ ...prev, [name]: value || "" }));
  }
};

  const handleBackgroundSelect = (path) => {
    setExerciseData((prev) => ({
      ...prev,
      background_image_path: path,
    }));
    setBackgroundImageFile(null);
    setShowBackgroundSelector(false);
    Swal.fire({
      title: t("success"),
      text: t("eventForm.success.backgroundSelected"),
      icon: "success",
      confirmButtonColor: "var(--primary-color)",
      background: "var(--card-background)",
    });
  };

  const handleDeleteFile = (type) => {
    if (type === "help_image") {
      setExerciseData((prev) => ({ ...prev, help_image: null }));
      helpImageRef.current.value = null;
    } else if (type === "background_image") {
      setExerciseData((prev) => ({ ...prev, background_image_path: null }));
      setBackgroundImageFile(null);
      if (backgroundImageRef.current) backgroundImageRef.current.value = null;
    } else if (type === "table4") {
      setQuestionFiles((prev) => {
        const updated = [...prev];
        updated[currentQuestionIndex] = {
          ...updated[currentQuestionIndex],
          table4: null,
        };
        return updated;
      });
      setQuestionData((prev) => ({ ...prev, table4: null }));
      table4Ref.current.value = null;
    } else if (type === "consigne_audio") {
      setExerciseData((prev) => ({ ...prev, consigne_audio: null }));
      consigneAudioRef.current.value = null;
    } else if (type === "song_audio") {
      setExerciseData((prev) => ({ ...prev, song_audio: null }));
      songAudioRef.current.value = null;
    } else if (type === "hmed_audio") {
      setQuestionFiles((prev) => {
        const updated = [...prev];
        updated[currentQuestionIndex] = {
          ...updated[currentQuestionIndex],
          hmed_audio: null,
        };
        return updated;
      });
      setQuestionData((prev) => ({ ...prev, hmed_audio: null }));
      hmedAudioRef.current.value = null;
    } else if (type === "Isac_audio") {
      setQuestionFiles((prev) => {
        const updated = [...prev];
        updated[currentQuestionIndex] = {
          ...updated[currentQuestionIndex],
          Isac_audio: null,
        };
        return updated;
      });
      setQuestionData((prev) => ({ ...prev, Isac_audio: null }));
      IsacAudioRef.current.value = null;
    }
    Swal.fire({
      title: t("success"),
      text: t("eventForm.success.fileDeleted"),
      icon: "success",
      confirmButtonColor: "var(--primary-color)",
      background: "var(--card-background)",
    });
  };

  const handleQuestionChange = (e) => {
    const { name, value, files } = e.target;
    setErrorField((prev) => (prev === name ? null : prev));
    if (files && (name === "table4" || name === "hmed_audio" || name === "Isac_audio")) {
      const file = files[0];
      if (!file) return;
      const maxSize = name === "table4" ? MAX_TABLE4_SIZE : MAX_FILE_SIZE;
      if (file.size > maxSize) {
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: t("fileTooLarge", { size: maxSize / (1024 * 1024) }),
          confirmButtonColor: "var(--primary-color)",
          background: "var(--card-background)",
        });
        e.target.value = null;
        if (name === "table4") table4Ref.current.value = null;
        else if (name === "hmed_audio") hmedAudioRef.current.value = null;
        else IsacAudioRef.current.value = null;
        return;
      }
      if (
        name === "table4" &&
        !["image/jpeg", "image/png", "image/jpg", "image/gif"].includes(file.type)
      ) {
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: t("invalidImageType.table4"),
          confirmButtonColor: "var(--primary-color)",
          background: "var(--card-background)",
        });
        e.target.value = null;
        table4Ref.current.value = null;
        return;
      }
      if ((name === "hmed_audio" || name === "Isac_audio") && file.type !== "audio/wav") {
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: t(`invalidAudioType.${name === "hmed_audio" ? "hmed" : "isac"}`),
          confirmButtonColor: "var(--primary-color)",
          background: "var(--card-background)",
        });
        e.target.value = null;
        if (name === "hmed_audio") hmedAudioRef.current.value = null;
        else IsacAudioRef.current.value = null;
        return;
      }
      setQuestionFiles((prev) => {
        const updated = [...prev];
        updated[currentQuestionIndex] = {
          ...updated[currentQuestionIndex],
          [name]: file,
        };
        return updated;
      });
      setQuestionData((prev) => ({ ...prev, [name]: file }));
    } else {
      setQuestionData((prev) => ({ ...prev, [name]: value || "" }));
    }
  };

  const handleFonttypeIconClick = () => {
    if (fonttypeSelectRef.current) {
      fonttypeSelectRef.current.focus();
      fonttypeSelectRef.current.click();
    }
  };

  const handleIconClick = () => {
    if (selectRef.current) {
      selectRef.current.focus();
      selectRef.current.click();
    }
  };

  const markFieldError = (fieldName) => {
    setErrorField(fieldName);
    requestAnimationFrame(() => {
      const el = document.querySelector(`[name="${fieldName}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
  };

  const handleNext = () => {
    setErrorField(null);
    if (currentQuestionIndex === -1) {
      if (!exerciseData.name.trim()) {
        markFieldError("name");
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: t("exerciseNameRequired"),
          confirmButtonColor: "var(--primary-color)",
          background: "var(--card-background)",
        });
        return;
      }
      if (!exerciseData.type) {
        markFieldError("type");
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: t("exerciseTypeRequired"),
          confirmButtonColor: "var(--primary-color)",
          background: "var(--card-background)",
        });
        return;
      }
      if (!exerciseData.fonttype) {
        markFieldError("fonttype");
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: t("fontTypeRequired"),
          confirmButtonColor: "var(--primary-color)",
          background: "var(--card-background)",
        });
        return;
      }
      const firstQuestion = questions[0];
      setQuestionData({
        T1: firstQuestion.T1 || "",
        T2: firstQuestion.T2 || "",
        T3: firstQuestion.T3 || "",
        T4: firstQuestion.T4 || "",
        T5: firstQuestion.T5 || "",
        T6: firstQuestion.T6 || "",
        T7: firstQuestion.T7 || "",
        T9: firstQuestion.T9 || "",
        fr1: firstQuestion.fr1 || "",
        table4: firstQuestion.table4 || null,
        hmed_audio: firstQuestion.hmed_audio || null,
        Isac_audio: firstQuestion.Isac_audio || null,
        reponse: firstQuestion.reponse || "",
        reponse1: firstQuestion.reponse1 || "",
        reponse2: firstQuestion.reponse2 || "",
        phrase_finale: firstQuestion.phrase_finale || "",
      });
      setCurrentQuestionIndex(0);
    } else if (currentQuestionIndex < MAX_QUESTIONS - 1) {
      if (exerciseData.type === "clavier_maths" || exerciseData.type === "clavier_langue") {
        if (!questionData.T7.trim()) {
          markFieldError("T7");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("questionT7Required", {
              type: exerciseData.type === "clavier_maths" ? t("clavier_maths") : t("clavier_langue"),
            }),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!questionData.reponse.trim()) {
          markFieldError("reponse");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("correctAnswerRequired", {
              type: exerciseData.type === "clavier_maths" ? t("clavier_maths") : t("clavier_langue"),
            }),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (exerciseData.type === "clavier_langue" && !exerciseData.isFrench && !exerciseData.isArabic && !questionData.fr1.trim()) {
          markFieldError("fr1");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("frenchPhraseRequired"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
      } else if (exerciseData.type === "clavier2inputs") {
        if (!questionData.T7.trim()) {
          markFieldError("T7");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("questionT7Required", { type: t("clavier2inputs") }),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!questionData.reponse1.trim()) {
          markFieldError("reponse1");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("firstCorrectAnswerRequired"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!questionData.reponse2.trim()) {
          markFieldError("reponse2");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("secondCorrectAnswerRequired"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
      } else if (exerciseData.type === "quizz") {
        if (!questionData.T1.trim()) {
          markFieldError("T1");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("choiceT1Required"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!questionData.T2.trim()) {
          markFieldError("T2");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("choiceT2Required"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!questionData.T7.trim()) {
          markFieldError("T7");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("questionT7Required", { type: t(exerciseData.type) }),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!questionData.T9.trim()) {
          markFieldError("T9");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("correctPhraseRequired"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!questionData.reponse.trim()) {
          markFieldError("reponse");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("correctAnswerRequired", { type: t(exerciseData.type) }),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
      } else if (exerciseData.type === "click" || exerciseData.type === "click_3_choix" || exerciseData.type === "click_4_choix") {
        if (!questionData.T1.trim()) {
          markFieldError("T1");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("choiceT1Required"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!questionData.reponse.trim()) {
          markFieldError("reponse");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("correctAnswerRequired", { type: t(exerciseData.type) }),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!exerciseData.isFrench && !exerciseData.isArabic && !questionData.fr1.trim()) {
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("frenchPhraseRequired"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
      } else if (exerciseData.type === "click_phrase" || exerciseData.type === "click_3_phrase" || exerciseData.type === "click_4_phrase") {
        if (!questionData.T1.trim()) {
          markFieldError("T1");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("choiceT1Required"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!questionData.T7.trim()) {
          markFieldError("T7");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("phraseRequired"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!questionData.T9.trim()) {
          markFieldError("T9");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("correctPhraseRequired"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!questionData.reponse.trim()) {
          markFieldError("reponse");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("correctAnswerRequired", { type: t(exerciseData.type) }),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!exerciseData.isFrench && !exerciseData.isArabic && !questionData.fr1.trim()) {
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("frenchPhraseRequired"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
      } else if (exerciseData.type === "dropbox" || exerciseData.type === "dropbox_langue") {
        if (!questionData.T1.trim()) {
          markFieldError("T1");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("choiceT1Required"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!questionData.T7.trim()) {
          markFieldError("T7");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("phraseRequired"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!questionData.reponse.trim()) {
          markFieldError("reponse");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("correctAnswerRequired", { type: t(exerciseData.type) }),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (exerciseData.type === "dropbox_langue" && !questionData.fr1.trim()) {
          markFieldError("fr1");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("frenchPhraseRequired"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
      } else if (exerciseData.type === "drag_and_drop_phrase" || exerciseData.type === "drag_and_drop_paragraphe") {
        if (!questionData.T1.trim()) {
          markFieldError("T1");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("choiceT1Required"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!questionData.T2.trim()) {
          markFieldError("T2");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("choiceT2Required"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!questionData.T3.trim()) {
          markFieldError("T3");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("choiceT3Required"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!questionData.T4.trim()) {
          markFieldError("T4");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("choiceT4Required"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!questionData.T5.trim()) {
          markFieldError("T5");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("choiceT5Required"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!questionData.T6.trim()) {
          markFieldError("T6");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("choiceT6Required"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!questionData.T7.trim()) {
          markFieldError("T7");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("phraseRequired"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (!questionData.T9.trim()) {
          markFieldError("T9");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("correctPhraseRequired"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
        if (exerciseData.type === "drag_and_drop_phrase" && !exerciseData.isFrench && !exerciseData.isArabic && !questionData.fr1.trim()) {
          markFieldError("fr1");
          Swal.fire({
            icon: "error",
            title: t("error"),
            text: t("frenchPhraseRequired"),
            confirmButtonColor: "var(--primary-color)",
            background: "var(--card-background)",
          });
          return;
        }
      }
      const updatedQuestions = questions.map((q, index) =>
        index === currentQuestionIndex ? { ...questionData } : q
      );
      setQuestions(updatedQuestions);
      setExerciseData((prev) => ({ ...prev, questions: updatedQuestions }));

      const nextQuestion = updatedQuestions[currentQuestionIndex + 1];
      setQuestionData({
        T1: nextQuestion.T1 || "",
        T2: nextQuestion.T2 || "",
        T3: nextQuestion.T3 || "",
        T4: nextQuestion.T4 || "",
        T5: nextQuestion.T5 || "",
        T6: nextQuestion.T6 || "",
        T7: nextQuestion.T7 || "",
        T9: nextQuestion.T9 || "",
        fr1: nextQuestion.fr1 || "",
        table4: nextQuestion.table4 || null,
        hmed_audio: nextQuestion.hmed_audio || null,
        Isac_audio: nextQuestion.Isac_audio || null,
        reponse: nextQuestion.reponse || "",
        reponse1: nextQuestion.reponse1 || "",
        reponse2: nextQuestion.reponse2 || "",
        phrase_finale: nextQuestion.phrase_finale || "",
      });
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handleBack = () => {
    setErrorField(null);
    if (currentQuestionIndex > 0) {
      const updatedQuestions = questions.map((q, index) =>
        index === currentQuestionIndex ? { ...questionData } : q
      );
      setQuestions(updatedQuestions);
      setExerciseData((prev) => ({ ...prev, questions: updatedQuestions }));

      const prevQuestion = updatedQuestions[currentQuestionIndex - 1];
      setQuestionData({
        T1: prevQuestion.T1 || "",
        T2: prevQuestion.T2 || "",
        T3: prevQuestion.T3 || "",
        T4: prevQuestion.T4 || "",
        T5: prevQuestion.T5 || "",
        T6: prevQuestion.T6 || "",
        T7: prevQuestion.T7 || "",
        T9: prevQuestion.T9 || "",
        fr1: prevQuestion.fr1 || "",
        table4: prevQuestion.table4 || null,
        hmed_audio: prevQuestion.hmed_audio || null,
        Isac_audio: prevQuestion.Isac_audio || null,
        reponse: prevQuestion.reponse || "",
        reponse1: prevQuestion.reponse1 || "",
        reponse2: prevQuestion.reponse2 || "",
        phrase_finale: prevQuestion.phrase_finale || "",
      });
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    } else if (currentQuestionIndex === 0) {
      const updatedQuestions = questions.map((q, index) =>
        index === currentQuestionIndex ? { ...questionData } : q
      );
      setQuestions(updatedQuestions);
      setExerciseData((prev) => ({ ...prev, questions: updatedQuestions }));
      setCurrentQuestionIndex(-1);
    }
  };

  const handleSaveAndRedirect = async () => {
    try {
      setIsSubmitting(true);
      setErrorField(null);
      if (!exerciseData.name.trim()) {
        markFieldError("name");
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: t("exerciseNameRequired"),
          confirmButtonColor: "var(--primary-color)",
          background: "var(--card-background)",
        });
        return;
      }
      if (!exerciseData.type) {
        markFieldError("type");
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: t("exerciseTypeRequired"),
          confirmButtonColor: "var(--primary-color)",
          background: "var(--card-background)",
        });
        return;
      }
      if (!exerciseData.fonttype) {
        markFieldError("fonttype");
        Swal.fire({
          icon: "error",
          title: t("error"),
          text: t("fontTypeRequired"),
          confirmButtonColor: "var(--primary-color)",
          background: "var(--card-background)",
        });
        return;
      }

      let updatedQuestions = [...questions];
      if (currentQuestionIndex >= 0) {
        updatedQuestions = questions.map((q, index) =>
          index === currentQuestionIndex ? { ...questionData } : q
        );
        setQuestions(updatedQuestions);
        setExerciseData((prev) => ({ ...prev, questions: updatedQuestions }));
      }

      const formData = new FormData();
      formData.append("name", exerciseData.name.trim());
      if (exerciseData.consigne) formData.append("consigne", exerciseData.consigne);
      formData.append("type", exerciseData.type);
      formData.append("fonttype", exerciseData.fonttype);
      if (exerciseData.per_link) formData.append("per_link", exerciseData.per_link.trim());
      formData.append("isfrench", exerciseData.isFrench);
      formData.append("isarabic", exerciseData.isArabic);

      if (exerciseData.help_image instanceof File) {
        formData.append("help_image", exerciseData.help_image);
        console.log("Appending help_image:", exerciseData.help_image.name);
      }
      if (backgroundImageFile instanceof File) {
        formData.append("background_image", backgroundImageFile);
        console.log("Appending background_image:", backgroundImageFile.name);
      } else if (exerciseData.background_image_path && typeof exerciseData.background_image_path === "string") {
        formData.append("background_image_path", exerciseData.background_image_path);
        console.log("Appending background_image_path:", exerciseData.background_image_path);
      }
      if (exerciseData.consigne_audio instanceof File) {
        formData.append("consigne_audio", exerciseData.consigne_audio);
        console.log("Appending consigne_audio:", exerciseData.consigne_audio.name);
      }
      if (exerciseData.song_audio instanceof File) {
        formData.append("song_audio", exerciseData.song_audio);
        console.log("Appending song_audio:", exerciseData.song_audio.name);
      }

      const validQuestions = updatedQuestions.filter((q) =>
        exerciseData.type === "clavier_maths" || exerciseData.type === "clavier_langue"
          ? q.T7.trim() && q.reponse.trim() && (exerciseData.type !== "clavier_langue" || exerciseData.isFrench || exerciseData.isArabic || q.fr1.trim())
          : exerciseData.type === "clavier2inputs"
          ? q.T7.trim() && q.reponse1.trim() && q.reponse2.trim()
          : exerciseData.type === "quizz"
          ? q.T1.trim() && q.T2.trim() && q.T7.trim() && q.T9.trim() && q.reponse.trim()
          : exerciseData.type === "click" || exerciseData.type === "click_3_choix" || exerciseData.type === "click_4_choix"
          ? q.T1.trim() && q.reponse.trim() && (exerciseData.isFrench || exerciseData.isArabic || q.fr1.trim())
          : exerciseData.type === "click_phrase" || exerciseData.type === "click_3_phrase" || exerciseData.type === "click_4_phrase"
          ? q.T1.trim() && q.T7.trim() && q.T9.trim() && q.reponse.trim() && (exerciseData.isFrench || exerciseData.isArabic || q.fr1.trim())
          : exerciseData.type === "dropbox"
          ? q.T1.trim() && q.T7.trim() && q.reponse.trim()
          : exerciseData.type === "dropbox_langue"
          ? q.T1.trim() && q.T7.trim() && q.reponse.trim() && q.fr1.trim()
          : exerciseData.type === "drag_and_drop_phrase" || exerciseData.type === "drag_and_drop_paragraphe"
          ? q.T1.trim() && q.T2.trim() && q.T3.trim() && q.T4.trim() && q.T5.trim() && q.T6.trim() && q.T7.trim() && q.T9.trim() && (exerciseData.type !== "drag_and_drop_phrase" || exerciseData.isFrench || exerciseData.isArabic || q.fr1.trim())
          : q.T1.trim() && q.reponse.trim()
      );

      validQuestions.forEach((question, index) => {
        if (
          exerciseData.type !== "clavier_maths" &&
          exerciseData.type !== "clavier_langue" &&
          exerciseData.type !== "clavier2inputs"
        ) {
          formData.append(`questions[${index}][T1]`, question.T1.trim());
          if (
            exerciseData.type === "click" ||
            exerciseData.type === "click_3_choix" ||
            exerciseData.type === "click_4_choix" ||
            exerciseData.type === "dropbox" ||
            exerciseData.type === "dropbox_langue" ||
            exerciseData.type === "click_phrase" ||
            exerciseData.type === "click_3_phrase" ||
            exerciseData.type === "click_4_phrase" ||
            exerciseData.type === "drag_and_drop_phrase" ||
            exerciseData.type === "drag_and_drop_paragraphe" ||
            exerciseData.type === "quizz"
          ) {
            if (question.T2.trim()) formData.append(`questions[${index}][T2]`, question.T2.trim());
          }
          if (
            exerciseData.type === "click_3_choix" ||
            exerciseData.type === "click_4_choix" ||
            exerciseData.type === "dropbox" ||
            exerciseData.type === "dropbox_langue" ||
            exerciseData.type === "click_3_phrase" ||
            exerciseData.type === "click_4_phrase" ||
            exerciseData.type === "drag_and_drop_phrase" ||
            exerciseData.type === "drag_and_drop_paragraphe"
          ) {
            if (question.T3.trim()) formData.append(`questions[${index}][T3]`, question.T3.trim());
          }
          if (
            exerciseData.type === "click_4_choix" ||
            exerciseData.type === "click_4_phrase" ||
            exerciseData.type === "drag_and_drop_phrase" ||
            exerciseData.type === "drag_and_drop_paragraphe"
          ) {
            if (question.T4.trim()) formData.append(`questions[${index}][T4]`, question.T4.trim());
          }
          if (exerciseData.type === "drag_and_drop_phrase" || exerciseData.type === "drag_and_drop_paragraphe") {
            if (question.T5.trim()) formData.append(`questions[${index}][T5]`, question.T5.trim());
            if (question.T6.trim()) formData.append(`questions[${index}][T6]`, question.T6.trim());
          }
        }
        if (
          exerciseData.type === "dropbox" ||
          exerciseData.type === "dropbox_langue" ||
          exerciseData.type === "click_phrase" ||
          exerciseData.type === "click_3_phrase" ||
          exerciseData.type === "click_4_phrase" ||
          exerciseData.type === "drag_and_drop_phrase" ||
          exerciseData.type === "drag_and_drop_paragraphe" ||
          exerciseData.type === "clavier_maths" ||
          exerciseData.type === "clavier_langue" ||
          exerciseData.type === "clavier2inputs" ||
          exerciseData.type === "quizz"
        ) {
          if (question.T7.trim()) formData.append(`questions[${index}][T7]`, question.T7.trim());
        }
        if (
          exerciseData.type === "click_phrase" ||
          exerciseData.type === "click_3_phrase" ||
          exerciseData.type === "click_4_phrase" ||
          exerciseData.type === "dropbox" ||
          exerciseData.type === "dropbox_langue" ||
          exerciseData.type === "drag_and_drop_phrase" ||
          exerciseData.type === "drag_and_drop_paragraphe" ||
          exerciseData.type === "clavier2inputs" ||
          exerciseData.type === "quizz"
        ) {
          if (question.T9.trim()) formData.append(`questions[${index}][T9]`, question.T9.trim());
        }
        if (
          exerciseData.type === "dropbox_langue" ||
          exerciseData.type === "clavier_langue" ||
          exerciseData.type === "click" ||
          exerciseData.type === "click_3_choix" ||
          exerciseData.type === "click_4_choix" ||
          exerciseData.type === "drag_and_drop_phrase" ||
          (!exerciseData.isFrench && !exerciseData.isArabic &&
            (exerciseData.type === "click_phrase" ||
             exerciseData.type === "click_3_phrase" ||
             exerciseData.type === "click_4_phrase"))
        ) {
          if (question.fr1.trim()) formData.append(`questions[${index}][fr1]`, question.fr1.trim());
        }
        if (questionFiles[index]?.table4 instanceof File) {
          formData.append(`questions[${index}][table4]`, questionFiles[index].table4);
          console.log(`Appending questions[${index}][table4]:`, questionFiles[index].table4.name);
        }
        if (questionFiles[index]?.hmed_audio instanceof File) {
          formData.append(`questions[${index}][hmed_audio]`, questionFiles[index].hmed_audio);
          console.log(`Appending questions[${index}][hmed_audio]:`, questionFiles[index].hmed_audio.name);
        }
        if (questionFiles[index]?.Isac_audio instanceof File) {
          formData.append(`questions[${index}][Isac_audio]`, questionFiles[index].Isac_audio);
          console.log(`Appending questions[${index}][Isac_audio]:`, questionFiles[index].Isac_audio.name);
        }
        if (exerciseData.type === "clavier2inputs") {
          if (question.reponse1.trim()) formData.append(`questions[${index}][reponse1]`, question.reponse1.trim());
          if (question.reponse2.trim()) formData.append(`questions[${index}][reponse2]`, question.reponse2.trim());
          if (question.phrase_finale.trim()) {
            formData.append(`questions[${index}][phrase_finale]`, question.phrase_finale.trim());
          }
        } else {
          if (question.reponse.trim()) formData.append(`questions[${index}][reponse]`, question.reponse.trim());
        }
      });

      const formDataObj = {};
      for (let [key, value] of formData.entries()) {
        formDataObj[key] = value instanceof File ? `File: ${value.name}` : value;
      }
      console.log("Sending FormData:", formDataObj);

      const response = await createExercise(formData);

      console.log("Create response:", response);

      if (response.exercise) {
        const draftId = location.state?.draft?.id;
        if (draftId) {
          const existingDraftsStr = localStorage.getItem("exerciseDrafts");
          if (existingDraftsStr) {
            const existingDrafts = JSON.parse(existingDraftsStr);
            const updatedDrafts = existingDrafts.filter((draft) => draft.id !== draftId);
            localStorage.setItem("exerciseDrafts", JSON.stringify(updatedDrafts));
          }
        }
        Swal.fire({
          title: t("success"),
          text: t("eventForm.success.exerciseCreated"),
          icon: "success",
          confirmButtonColor: "var(--primary-color)",
          background: "var(--card-background)",
        });
        navigate("/");
      } else {
        throw new Error(response.message || t("unexpectedResponse"));
      }
    } catch (err) {
      console.error("Error creating exercise:", {
        message: err.message,
        status: err.response?.status,
        data: err.response?.data,
      });
      Swal.fire({
        icon: "error",
        title: t("error"),
        text: err.response?.data?.message || err.message || t("createFailed"),
        html: err.response?.data?.errors
          ? `<p>${err.response.data.message}</p><ul>${Object.entries(
              err.response.data.errors
            )
              .map(([key, messages]) =>
                messages.map((msg) => `<li>${key}: ${msg}</li>`).join("")
              )
              .join("")}</ul>`
          : err.response?.data?.message || err.message || t("createFailed"),
        confirmButtonColor: "var(--primary-color)",
        background: "var(--card-background)",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToExercises = () => {
    navigate("/");
  };

  const UploadIcon = () => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="update-form-upload-icon"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path instanceof File) {
      return URL.createObjectURL(path);
    }
    const cleanPath = path.startsWith(STORAGE_BASE_URL)
      ? path.replace(STORAGE_BASE_URL, "").replace(/^\/+/, "")
      : path;
    if (cleanPath.startsWith("http")) {
      return cleanPath;
    }
    return `${STORAGE_BASE_URL}/${cleanPath}`;
  };

  const isExerciseScreen = currentQuestionIndex === -1;

  return (
    <div className="update-form-overlay">
      <div className="update-form-container">
        <button
          type="button"
          onClick={handleBackToExercises}
          className="update-form-back-button"
          title={t("backToExercises")}
        >
          <FontAwesomeIcon
            icon="fa-solid fa-circle-arrow-left"
            className="update-form-back-icon"
          />
        </button>
        <h2 className="update-form-title">
          {isExerciseScreen
            ? t("eventForm.title")
            : t("eventForm.questionTitle", { number: currentQuestionIndex + 1, total: MAX_QUESTIONS })}
        </h2>
        <div className="update-form-content">
          {isExerciseScreen ? (
            <div className="exercise-section">
              <h3 className="update-form-section-heading">{t("updateForm.exerciseDetailsSection")}</h3>
              <div>
                <label className="update-form-field-label">{t("updateForm.exerciseType")}</label>
                <div className={`update-form-custom-select-container${errorField === "type" ? " update-form-error" : ""}`}>
                <span className="update-form-custom-select-text">
                  {exerciseData?.type
                    ? t(`${exerciseData.type}`)
                    : t("ExerciseType")}
                </span>
                <FontAwesomeIcon
                  icon="fa-solid fa-caret-down"
                  className="update-form-caret-icon"
                  onClick={handleIconClick}
                />
                <select
                  name="type"
                  value={exerciseData?.type || ""}
                  onChange={handleExerciseChange}
                  ref={selectRef}
                  className="update-form-custom-select"
                >
                  <option value="" disabled>
                    {t("ExerciseType")}
                  </option>
                  <option value="dropbox">{t("Dropbox")}</option>
                  <option value="dropbox_langue">{t("Dropbox_langue")}</option>
                  <option value="click">{t("Click")}</option>
                  <option value="click_3_choix">{t("Click_3_choix")}</option>
                  <option value="click_4_choix">{t("Click_4_choix")}</option>
                  <option value="click_phrase">{t("Click_phrase")}</option>
                  <option value="click_3_phrase">{t("Click_3_phrase")}</option>
                  <option value="click_4_phrase">{t("Click_4_phrase")}</option>
                  <option value="drag_and_drop_phrase">{t("Drag_and_drop_phrase")}</option>
                  <option value="drag_and_drop_paragraphe">{t("Drag_and_drop_paragraphe")}</option>
                  <option value="clavier_maths">{t("Clavier_maths")}</option>
                  <option value="clavier_langue">{t("Clavier_langue")}</option>
                  <option value="clavier2inputs">{t("Clavier2inputs")}</option>
                  <option value="quizz">{t("Quizz")}</option>
                </select>
                </div>
              </div>
              <div>
                <label className="update-form-field-label">{t("updateForm.fontType")}</label>
                <div className={`update-form-custom-select-container${errorField === "fonttype" ? " update-form-error" : ""}`}>
                <span className="update-form-custom-select-text">
                  {exerciseData?.fonttype
                    ? t(`${exerciseData.fonttype}`)
                    : t("FontType")}
                </span>
                <FontAwesomeIcon
                  icon="fa-solid fa-caret-down"
                  className="update-form-caret-icon"
                  onClick={handleFonttypeIconClick}
                />
                <select
                  name="fonttype"
                  value={exerciseData?.fonttype || ""}
                  onChange={handleExerciseChange}
                  ref={fonttypeSelectRef}
                  className="update-form-custom-select"
                >
                  <option value="" disabled>
                    {t("FontType")}
                  </option>
                  <option value="small">{t("Small")}</option>
                  <option value="medium">{t("Medium")}</option>
                  <option value="large">{t("Large")}</option>
                </select>
                </div>
              </div>
              <div>
                <label className="update-form-field-label" htmlFor="event-form-name">{t("updateForm.exerciseName")}</label>
                <input
                  id="event-form-name"
                  type="text"
                  name="name"
                  value={exerciseData.name}
                  onChange={handleExerciseChange}
                  placeholder={t("ExerciseName")}
                  className={`update-form-input${errorField === "name" ? " update-form-error" : ""}`}
                />
              </div>
              <div>
                <label className="update-form-field-label" htmlFor="event-form-per-link">{t("updateForm.perLink")}</label>
                <input
                  id="event-form-per-link"
                  type="text"
                  name="per_link"
                  value={exerciseData.per_link}
                  onChange={handleExerciseChange}
                  placeholder={t("Per_Link")}
                  className="update-form-input"
                />
              </div>
              {(exerciseData.type === "click_phrase" ||
                exerciseData.type === "click_3_phrase" ||
                exerciseData.type === "click_4_phrase" ||
                exerciseData.type === "clavier_langue" ||
                exerciseData.type === "click" ||
                exerciseData.type === "click_3_choix" ||
                exerciseData.type === "click_4_choix" ||
                exerciseData.type === "drag_and_drop_phrase") && (
                <div className="update-form-checkbox-container">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      name="isFrench"
                      checked={exerciseData.isFrench}
                      onChange={handleExerciseChange}
                      className="update-form-checkbox"
                    />
                    <label className="update-form-checkbox-label">{t("IsFrenchExercise")}</label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <input
                      type="checkbox"
                      name="isArabic"
                      checked={exerciseData.isArabic}
                      onChange={handleExerciseChange}
                      className="update-form-checkbox"
                    />
                    <label className="update-form-checkbox-label">{t("IsArabicExercise")}</label>
                  </div>
                </div>
              )}
              <div>
                <label className="update-form-field-label">{t("updateForm.helpImage")}</label>
                <label className="update-form-custom-file-input">
                  <span className="update-form-file-placeholder">
                    <span className="update-form-file-text">
                      {exerciseData.help_image
                        ? exerciseData.help_image instanceof File
                          ? exerciseData.help_image.name
                          : exerciseData.help_image.split("/").pop()
                        : t("HelpImage")}
                    </span>
                    <UploadIcon />
                  </span>
                  <input
                    type="file"
                    name="help_image"
                    ref={helpImageRef}
                    onChange={handleExerciseChange}
                    accept="image/jpeg,image/png,image/jpg,image/gif"
                    className="update-form-file-input"
                  />
                </label>
                {exerciseData.help_image && (
                  <div className="update-form-image-container">
                    <img
                      src={getImageUrl(exerciseData.help_image)}
                      alt={t("helpImagePreview")}
                      className="update-form-preview-image"
                      onError={() => console.log("Failed to load help_image preview")}
                    />
                    <div className="update-form-audio-wrapper">
                      <button
                        type="button"
                        onClick={() => handleDeleteFile("help_image")}
                        className="update-form-delete-button"
                        title={t("delete")}
                      >
                        <FontAwesomeIcon icon="fa-solid fa-trash" />
                        <span>{t("delete")}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="update-form-field-label">{t("updateForm.backgroundImageLabel")}</label>
                <div
                  onClick={() => setShowBackgroundSelector(true)}
                  className="update-form-file-placeholder"
                >
                  <span className="update-form-file-text">
                    {backgroundImageFile
                      ? backgroundImageFile.name
                      : exerciseData.background_image_path
                      ? exerciseData.background_image_path.split("/").pop()
                      : t("BackgroundImage")}
                  </span>
                  <UploadIcon />
                </div>
                {(backgroundImageFile || exerciseData.background_image_path) && (
                  <div className="update-form-image-container">
                    <img
                      src={getImageUrl(backgroundImageFile || exerciseData.background_image_path)}
                      alt={t("backgroundPreview")}
                      className="update-form-preview-image"
                      onError={() => console.log("Failed to load background preview")}
                    />
                    <div className="update-form-audio-wrapper">
                      <button
                        type="button"
                        onClick={() => handleDeleteFile("background_image")}
                        className="update-form-delete-button"
                        title={t("delete")}
                      >
                        <FontAwesomeIcon icon="fa-solid fa-trash" />
                        <span>{t("delete")}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="update-form-field-label" htmlFor="event-form-consigne">{t("updateForm.consigne")}</label>
                <input
                  id="event-form-consigne"
                  type="text"
                  name="consigne"
                  value={exerciseData.consigne}
                  onChange={handleExerciseChange}
                  placeholder={t("Consigne")}
                  className="update-form-input"
                />
              </div>
              <div>
                <label className="update-form-field-label">{t("updateForm.consigneAudio")}</label>
                <label className="update-form-custom-file-input">
                  <span className="update-form-file-placeholder">
                    <span className="update-form-file-text">
                      {exerciseData.consigne_audio
                        ? exerciseData.consigne_audio instanceof File
                          ? exerciseData.consigne_audio.name
                          : exerciseData.consigne_audio.split("/").pop()
                        : t("ConsigneAudio")}
                    </span>
                    <UploadIcon />
                  </span>
                  <input
                    type="file"
                    name="consigne_audio"
                    ref={consigneAudioRef}
                    onChange={handleExerciseChange}
                    accept="audio/wav"
                    className="update-form-file-input"
                  />
                </label>
                {exerciseData.consigne_audio && (
                  <div className="update-form-audio-container">
                    <audio
                      controls
                      src={getImageUrl(exerciseData.consigne_audio)}
                      className="update-form-preview-audio"
                      onError={() => console.log("Failed to load consigne_audio preview")}
                    />
                    <div className="update-form-audio-wrapper">
                      <button
                        type="button"
                        onClick={() => handleDeleteFile("consigne_audio")}
                        className="update-form-delete-button"
                        title={t("delete")}
                      >
                        <FontAwesomeIcon icon="fa-solid fa-trash" />
                        <span>{t("delete")}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="update-form-field-label">{t("updateForm.songAudio")}</label>
                <label className="update-form-custom-file-input">
                  <span className="update-form-file-placeholder">
                    <span className="update-form-file-text">
                      {exerciseData.song_audio
                        ? exerciseData.song_audio instanceof File
                          ? exerciseData.song_audio.name
                          : exerciseData.song_audio.split("/").pop()
                        : t("SongAudio")}
                    </span>
                    <UploadIcon />
                  </span>
                  <input
                    type="file"
                    name="song_audio"
                    ref={songAudioRef}
                    onChange={handleExerciseChange}
                    accept="audio/wav"
                    className="update-form-file-input"
                  />
                </label>
                {exerciseData.song_audio && (
                  <div className="update-form-audio-container">
                    <audio
                      controls
                      src={getImageUrl(exerciseData.song_audio)}
                      className="update-form-preview-audio"
                      onError={() => console.log("Failed to load song_audio preview")}
                    />
                    <div className="update-form-audio-wrapper">
                      <button
                        type="button"
                        onClick={() => handleDeleteFile("song_audio")}
                        className="update-form-delete-button"
                        title={t("delete")}
                      >
                        <FontAwesomeIcon icon="fa-solid fa-trash" />
                        <span>{t("delete")}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="question-section">
              <h3 className="update-form-section-heading">{t("updateForm.questionDetailsSection")}</h3>
              {exerciseData.type !== "clavier_maths" &&
                exerciseData.type !== "clavier_langue" &&
                exerciseData.type !== "clavier2inputs" && (
                  <>
                    <div>
                      <label className="update-form-field-label">{t("updateForm.choice1")}</label>
                      <input
                        type="text"
                        name="T1"
                        value={questionData.T1}
                        onChange={handleQuestionChange}
                        placeholder={t("Choice1")}
                        className={`update-form-input${errorField === "T1" ? " update-form-error" : ""}`}
                      />
                    </div>
                    {(exerciseData.type === "click" ||
                      exerciseData.type === "click_3_choix" ||
                      exerciseData.type === "click_4_choix" ||
                      exerciseData.type === "dropbox" ||
                      exerciseData.type === "dropbox_langue" ||
                      exerciseData.type === "click_phrase" ||
                      exerciseData.type === "click_3_phrase" ||
                      exerciseData.type === "click_4_phrase" ||
                      exerciseData.type === "drag_and_drop_phrase" ||
                      exerciseData.type === "drag_and_drop_paragraphe" ||
                      exerciseData.type === "quizz") && (
                      <div>
                        <label className="update-form-field-label">{t("updateForm.choice2")}</label>
                        <input
                          type="text"
                          name="T2"
                          value={questionData.T2}
                          onChange={handleQuestionChange}
                          placeholder={t("Choice2")}
                          className={`update-form-input${errorField === "T2" ? " update-form-error" : ""}`}
                        />
                      </div>
                    )}
                    {(exerciseData.type === "click_3_choix" ||
                      exerciseData.type === "click_4_choix" ||
                      exerciseData.type === "dropbox" ||
                      exerciseData.type === "dropbox_langue" ||
                      exerciseData.type === "click_3_phrase" ||
                      exerciseData.type === "click_4_phrase" ||
                      exerciseData.type === "drag_and_drop_phrase" ||
                      exerciseData.type === "drag_and_drop_paragraphe") && (
                      <div>
                        <label className="update-form-field-label">{t("updateForm.choice3")}</label>
                        <input
                          type="text"
                          name="T3"
                          value={questionData.T3}
                          onChange={handleQuestionChange}
                          placeholder={t("Choice3")}
                          className={`update-form-input${errorField === "T3" ? " update-form-error" : ""}`}
                        />
                      </div>
                    )}
                    {(exerciseData.type === "click_4_choix" ||
                      exerciseData.type === "click_4_phrase" ||
                      exerciseData.type === "drag_and_drop_phrase" ||
                      exerciseData.type === "drag_and_drop_paragraphe") && (
                      <div>
                        <label className="update-form-field-label">{t("updateForm.choice4")}</label>
                        <input
                          type="text"
                          name="T4"
                          value={questionData.T4}
                          onChange={handleQuestionChange}
                          placeholder={t("Choice4")}
                          className={`update-form-input${errorField === "T4" ? " update-form-error" : ""}`}
                        />
                      </div>
                    )}
                    {(exerciseData.type === "drag_and_drop_phrase" ||
                      exerciseData.type === "drag_and_drop_paragraphe") && (
                      <>
                        <div>
                          <label className="update-form-field-label">{t("updateForm.choice5")}</label>
                          <input
                            type="text"
                            name="T5"
                            value={questionData.T5}
                            onChange={handleQuestionChange}
                            placeholder={t("Choice5")}
                            className={`update-form-input${errorField === "T5" ? " update-form-error" : ""}`}
                          />
                        </div>
                        <div>
                          <label className="update-form-field-label">{t("updateForm.choice6")}</label>
                          <input
                            type="text"
                            name="T6"
                            value={questionData.T6}
                            onChange={handleQuestionChange}
                            placeholder={t("Choice6")}
                            className={`update-form-input${errorField === "T6" ? " update-form-error" : ""}`}
                          />
                        </div>
                      </>
                    )}
                  </>
                )}
              {(exerciseData.type === "dropbox" ||
                exerciseData.type === "dropbox_langue" ||
                exerciseData.type === "click_phrase" ||
                exerciseData.type === "click_3_phrase" ||
                exerciseData.type === "click_4_phrase" ||
                exerciseData.type === "drag_and_drop_phrase" ||
                exerciseData.type === "drag_and_drop_paragraphe" ||
                exerciseData.type === "clavier_maths" ||
                exerciseData.type === "clavier_langue" ||
                exerciseData.type === "clavier2inputs" ||
                exerciseData.type === "quizz") && (
                <div>
                  <label className="update-form-field-label">
                    {exerciseData.type === "clavier_maths"
                      ? t("updateForm.mathExpression")
                      : t("updateForm.phrase")}
                  </label>
                  <input
                    type="text"
                    name="T7"
                    value={questionData.T7}
                    onChange={handleQuestionChange}
                    placeholder={
                      exerciseData.type === "clavier_maths"
                        ? t("MathExpression")
                        : t("Phrase")
                    }
                    className={`update-form-input${errorField === "T7" ? " update-form-error" : ""}`}
                  />
                </div>
              )}
              {(exerciseData.type === "click_phrase" ||
                exerciseData.type === "click_3_phrase" ||
                exerciseData.type === "click_4_phrase" ||
                exerciseData.type === "dropbox" ||
                exerciseData.type === "dropbox_langue" ||
                exerciseData.type === "drag_and_drop_phrase" ||
                exerciseData.type === "drag_and_drop_paragraphe" ||
                exerciseData.type === "clavier2inputs" ||
                exerciseData.type === "quizz") && (
                <div>
                  <label className="update-form-field-label">{t("updateForm.correctPhrase")}</label>
                  <input
                    type="text"
                    name="T9"
                    value={questionData.T9}
                    onChange={handleQuestionChange}
                    placeholder={t("CorrectPhrase")}
                    className={`update-form-input${errorField === "T9" ? " update-form-error" : ""}`}
                  />
                </div>
              )}
              {(exerciseData.type === "dropbox_langue" ||
                exerciseData.type === "clavier_langue" ||
                exerciseData.type === "click" ||
                exerciseData.type === "click_3_choix" ||
                exerciseData.type === "click_4_choix" ||
                exerciseData.type === "drag_and_drop_phrase" ||
                (!exerciseData.isFrench && !exerciseData.isArabic &&
                  (exerciseData.type === "click_phrase" ||
                   exerciseData.type === "click_3_phrase" ||
                   exerciseData.type === "click_4_phrase"))) && (
                <div>
                  <label className="update-form-field-label">{t("updateForm.frenchPhrase")}</label>
                  <input
                    type="text"
                    name="fr1"
                    value={questionData.fr1}
                    onChange={handleQuestionChange}
                    placeholder={t("FrenchPhrase")}
                    className={`update-form-input${errorField === "fr1" ? " update-form-error" : ""}`}
                  />
                </div>
              )}
              {(exerciseData.type === "click" ||
                exerciseData.type === "click_3_choix" ||
                exerciseData.type === "click_4_choix" ||
                exerciseData.type === "dropbox" ||
                exerciseData.type === "dropbox_langue" ||
                exerciseData.type === "clavier2inputs" ||
                exerciseData.type === "quizz") && (
                <div>
                  <label className="update-form-field-label">{t("updateForm.tableImage")}</label>
                  <label className="update-form-custom-file-input">
                    <span className="update-form-file-placeholder">
                      <span className="update-form-file-text">
                        {questionFiles[currentQuestionIndex]?.table4
                          ? questionFiles[currentQuestionIndex].table4.name
                          : questionData.table4
                          ? questionData.table4.split("/").pop()
                          : t("Image")}
                      </span>
                      <UploadIcon />
                    </span>
                    <input
                      type="file"
                      name="table4"
                      ref={table4Ref}
                      onChange={handleQuestionChange}
                      accept="image/jpeg,image/png,image/jpg,image/gif"
                      className="update-form-file-input"
                    />
                  </label>
                  {(questionData.table4 || questionFiles[currentQuestionIndex]?.table4) && (
                    <div className="update-form-image-container">
                      <img
                        src={getImageUrl(questionFiles[currentQuestionIndex]?.table4 || questionData.table4)}
                        alt={t("table4Preview")}
                        className="update-form-preview-image"
                        onError={() => console.log("Failed to load table4 preview")}
                      />
                      <div className="update-form-audio-wrapper">
                        <button
                          type="button"
                          onClick={() => handleDeleteFile("table4")}
                          className="update-form-delete-button"
                          title={t("delete")}
                        >
                          <FontAwesomeIcon icon="fa-solid fa-trash" />
                          <span>{t("delete")}</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              <div>
                <label className="update-form-field-label">{t("updateForm.phraseAudio")}</label>
                <label className="update-form-custom-file-input">
                  <span className="update-form-file-placeholder">
                    <span className="update-form-file-text">
                      {questionData.hmed_audio
                        ? questionData.hmed_audio instanceof File
                          ? questionData.hmed_audio.name
                          : questionData.hmed_audio.split("/").pop()
                        : t("Phrase_audio")}
                    </span>
                    <UploadIcon />
                  </span>
                  <input
                    type="file"
                    name="hmed_audio"
                    ref={hmedAudioRef}
                    onChange={handleQuestionChange}
                    accept="audio/wav"
                    className="update-form-file-input"
                  />
                </label>
                {questionData.hmed_audio && (
                  <div className="update-form-audio-container">
                    <audio
                      controls
                      src={getImageUrl(questionData.hmed_audio)}
                      className="update-form-preview-audio"
                      onError={() => console.log("Failed to load hmed_audio preview")}
                    />
                    <div className="update-form-audio-wrapper">
                      <button
                        type="button"
                        onClick={() => handleDeleteFile("hmed_audio")}
                        className="update-form-delete-button"
                        title={t("delete")}
                      >
                        <FontAwesomeIcon icon="fa-solid fa-trash" />
                        <span>{t("delete")}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="update-form-field-label">{t("updateForm.correctPhraseAudio")}</label>
                <label className="update-form-custom-file-input">
                  <span className="update-form-file-placeholder">
                    <span className="update-form-file-text">
                      {questionData.Isac_audio
                        ? questionData.Isac_audio instanceof File
                          ? questionData.Isac_audio.name
                          : questionData.Isac_audio.split("/").pop()
                        : t("Correct phrase_audio")}
                    </span>
                    <UploadIcon />
                  </span>
                  <input
                    type="file"
                    name="Isac_audio"
                    ref={IsacAudioRef}
                    onChange={handleQuestionChange}
                    accept="audio/wav"
                    className="update-form-file-input"
                  />
                </label>
                {questionData.Isac_audio && (
                  <div className="update-form-audio-container">
                    <audio
                      controls
                      src={getImageUrl(questionData.Isac_audio)}
                      className="update-form-preview-audio"
                      onError={() => console.log("Failed to load Isac_audio preview")}
                    />
                    <div className="update-form-audio-wrapper">
                      <button
                        type="button"
                        onClick={() => handleDeleteFile("Isac_audio")}
                        className="update-form-delete-button"
                        title={t("delete")}
                      >
                        <FontAwesomeIcon icon="fa-solid fa-trash" />
                        <span>{t("delete")}</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
              {exerciseData.type === "clavier2inputs" && (
                <div>
                  <label className="update-form-field-label">{t("updateForm.finalPhrase")}</label>
                  <input
                    type="text"
                    name="phrase_finale"
                    value={questionData.phrase_finale || ""}
                    onChange={handleQuestionChange}
                    placeholder={t("FinalPhrase")}
                    className="update-form-input"
                  />
                </div>
              )}
              {exerciseData.type !== "clavier2inputs" && (
                <div>
                  <label className="update-form-field-label">{t("updateForm.correctChoice")}</label>
                  <input
                    type="text"
                    name="reponse"
                    value={questionData.reponse || ""}
                    onChange={handleQuestionChange}
                    placeholder={t("CorrectChoice")}
                    className={`update-form-input${errorField === "reponse" ? " update-form-error" : ""}`}
                  />
                </div>
              )}
              {exerciseData.type === "clavier2inputs" && (
                <>
                  <div>
                    <label className="update-form-field-label">{t("updateForm.firstCorrectAnswer")}</label>
                    <input
                      type="text"
                      name="reponse1"
                      value={questionData.reponse1 || ""}
                      onChange={handleQuestionChange}
                      placeholder={t("FirstCorrectAnswer")}
                      className={`update-form-input${errorField === "reponse1" ? " update-form-error" : ""}`}
                    />
                  </div>
                  <div>
                    <label className="update-form-field-label">{t("updateForm.secondCorrectAnswer")}</label>
                    <input
                      type="text"
                      name="reponse2"
                      value={questionData.reponse2 || ""}
                      onChange={handleQuestionChange}
                      placeholder={t("SecondCorrectAnswer")}
                      className={`update-form-input${errorField === "reponse2" ? " update-form-error" : ""}`}
                    />
                  </div>
                </>
              )}
            </div>
          )}
          <div className="update-form-button-container">
            <div className="update-form-button-group">
              <button
                type="button"
                onClick={handleBack}
                disabled={currentQuestionIndex === -1}
                className="update-form-button update-form-back-button-action"
              >
                {t("back")}
              </button>
              <button
                type="button"
                onClick={handleSaveAsDraft}
                disabled={isSubmitting}
                className="update-form-button update-form-draft-button"
              >
                {t("saveDraft")}
              </button>
              {currentQuestionIndex < MAX_QUESTIONS - 1 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="update-form-button update-form-next-button"
                >
                  {t("next")}
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSaveAndRedirect}
                  disabled={isSubmitting}
                  className="update-form-button update-form-save-button"
                >
                  {isSubmitting ? t("saving") : t("Save And Finish")}
                </button>
              )}
            </div>
          </div>
        </div>
        {showBackgroundSelector && (
          <BackgroundSelector
            onSelect={handleBackgroundSelect}
            onClose={() => setShowBackgroundSelector(false)}
          />
        )}
      </div>
    </div>
  );
};

export default EventForm;